import { useCallback, useEffect, useState, useMemo, useReducer, useRef } from 'react';
import yaml from 'js-yaml';
import { useRouter } from '@bymason-private/route-provider';
import { useAuth } from '@bymason-private/auth-provider';
import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Text,
  Divider,
  ControllerAlert as Alert,
  Box,
  Modal,
  CircularProgress,
} from '@bymason-private/legacy-pattern-components';
import { useToast } from '@bymason-private/controller-shared-widgets';
import {
  ArtifactModel,
  ArtifactType,
  ConfigModel,
  GetArtifactsOptionsList,
  JobModel,
  useCreateArtifact,
  useGetConfigArtifact,
} from '@bymason/controller-api-hooks';
import AppsStep from './steps/apps';
import MediaStep from './steps/media';
import ConfigureStep from './steps/configure';
import ReviewStep from './steps/review';
import { routes } from '../../../router';
import {
  stepViews,
  getStepIndex,
  getBaseProjectConfig,
  configReducer,
  ConfigActionTypes,
  StepId,
  StepView,
} from './logic';

export type { StepId } from './logic';

const useStyles = makeStyles(() => ({
  root: {
    marginBottom: 16,
    marginTop: 16,
    position: 'relative',
  },
  heading: {
    marginBottom: 16,
  },
  steps: {
    display: 'grid',
    gridTemplateColumns: `200px 1fr`,
    paddingBottom: 50,
  },
  stepActions: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    '& button': {
      boxShadow: 'none',
    },
    '& button:hover': {
      boxShadow: 'none',
    },
  },
  toolbar: {
    backgroundColor: 'black',
    position: 'fixed',
    height: 56,
    left: 168,
    right: 0,
    bottom: 0,
    textAlign: 'right',
    padding: '8px',
  },
}));

export default function BuildStepper({
  projectId,
  projectName,
  projectBuilds,
  stepId,
  onCreateBuildSuccess,
  onShowBuildRating,
}: BuildStepperProps) {
  const classes = useStyles();
  const { authToken = '', customerId } = useAuth();
  const { navigate } = useRouter();
  const [setToastMessage, setToastSeverity] = useToast();
  const [getConfig, configArtifact, configError, configBusy] = useGetConfigArtifact(authToken);
  const [uploadArtifact, uploadResult, uploadError, uploadBusy] = useCreateArtifact(authToken);
  const initConfig = useRef(getBaseProjectConfig(projectName));
  const [steps, setSteps] = useState<StepView[]>(stepViews);
  const [buildPending, setBuildPending] = useState(false);
  const [invalidArtifacts, setInvalidArtifacts] = useState<GetArtifactsOptionsList>([]);
  const [activeStep, setActiveStep] = useState(getStepIndex(stepId));
  const [config, dispatchConfig] = useReducer(configReducer, initConfig.current);

  const configIsValid = useMemo(() => {
    return invalidArtifacts.length === 0;
  }, [invalidArtifacts]);

  const lastBuild = useMemo(() => {
    return projectBuilds && projectBuilds.length > 0
      ? projectBuilds.reduce((prev, curr) => {
          return curr.configVersion > prev.configVersion ? curr : prev;
        })
      : null;
  }, [projectBuilds]);

  const isInitialConfig = useMemo(() => {
    // TODO: switch to deep equal function if not performant
    return JSON.stringify(initConfig.current) === JSON.stringify(config);
  }, [config]);

  const disableStepAtIdx = useCallback(
    (stepIdx) => {
      return (
        (stepIdx > getStepIndex('config') && isInitialConfig) ||
        (stepIdx > getStepIndex('review') && !configIsValid)
      );
    },
    [isInitialConfig, configIsValid]
  );

  const handleStep = useCallback(
    (stepIndex: number) => () => {
      if (stepIndex <= activeStep + 1) {
        setSteps((prevSteps) => {
          return prevSteps.map((step, idx) =>
            idx < stepIndex ? { ...step, complete: true } : { ...step, complete: false }
          );
        });
        const nextStep = stepViews[stepIndex].type;
        navigate(routes.ProjectBuildStepper, { projectId, stepId: nextStep });
      }
    },
    [navigate, projectId, activeStep]
  );

  const handleSelectApps = (selectedArtifacts: ArtifactModel[]) => {
    const apps = selectedArtifacts.reduce<Map<string, ConfigApkInfo>>((acc, art) => {
      return art.type === ArtifactType.Apk
        ? acc.set(art.name, {
            name: art.name,
            package_name: art.apk.packageName,
            version_code: Number(art.apk.versionCode),
          })
        : acc;
    }, new Map<string, ConfigApkInfo>());
    dispatchConfig({ type: ConfigActionTypes.ChangeApps, payload: [...apps.values()] });
  };

  const handleSelectSplash = (selectedArtifacts: ArtifactModel[]) => {
    const art = selectedArtifacts[0];
    const media =
      art?.type === ArtifactType.Media ? { name: art.name, version: Number(art.version) } : undefined;
    dispatchConfig({ type: ConfigActionTypes.ChangeSplash, payload: media });
  };

  const handleSelectAnimation = (selectedArtifacts: ArtifactModel[]) => {
    const art = selectedArtifacts[0];
    const media =
      art?.type === ArtifactType.Media ? { name: art.name, version: Number(art.version) } : undefined;
    dispatchConfig({ type: ConfigActionTypes.ChangeBootanimation, payload: media });
  };

  const handleConfigChange = useCallback(
    (config?: ConfigModel) => {
      const baseConfig = getBaseProjectConfig(projectName);
      const newConfig = config ? { ...config, os: { ...config.os, ...baseConfig.os } } : baseConfig;
      dispatchConfig({ type: ConfigActionTypes.ReplaceConfig, payload: newConfig });
    },
    [projectName]
  );

  const handleBuild = useCallback(() => {
    // Must set os.version to integer in api payload. os.version = 0 will tell backend to auto increment the latest
    // config version. Possibly this endpoint will always auto increment to a new version. Discussions pending.
    const newConfig: ConfigModel | undefined = config
      ? { ...config, os: { ...config.os, version: 0 } }
      : undefined;
    const yamlConfig = newConfig ? yaml.dump(newConfig, { lineWidth: -1 }) : undefined;
    if (yamlConfig && configIsValid) {
      const fileName = `${projectName}.yml`;
      const file = new File([yamlConfig], fileName);
      setBuildPending(true);
      uploadArtifact({
        type: 'config',
        file,
        fileName,
      });
    }
  }, [projectName, config, configIsValid, uploadArtifact]);

  const handleCancel = useCallback(() => {
    navigate(routes.Project, { projectId });
  }, [projectId, navigate]);

  const handleInvalidArtifacts = useCallback((invalid: GetArtifactsOptionsList) => {
    setInvalidArtifacts(invalid);
  }, []);

  useEffect(() => {
    if (projectId) {
      const routeStepIndex = getStepIndex(stepId);
      if (routeStepIndex === -1) {
        const nextStep = stepViews[0].type;
        navigate(routes.ProjectBuildStepper, { projectId, stepId: nextStep });
      } else {
        setActiveStep(routeStepIndex);
      }
    }
  }, [activeStep, navigate, projectId, stepId]);

  useEffect(() => {
    // get the actual config from the latest build
    if (projectName && customerId && lastBuild) {
      getConfig({ customerId, name: projectName, version: lastBuild.configVersion });
    }
  }, [customerId, getConfig, lastBuild, projectName]);

  useEffect(() => {
    // when the config from the latest build is retrieved, start the stepper with it
    if (projectName && lastBuild && isInitialConfig && configArtifact && !configBusy) {
      handleConfigChange(configArtifact?.config);
    }
  }, [isInitialConfig, configBusy, configArtifact, handleConfigChange, customerId, lastBuild, projectName]);

  useEffect(() => {
    if (uploadResult && projectName) {
      setToastMessage(
        `A new build for ${projectName} was successfully submitted. Refresh page for build status updates`
      );
      setToastSeverity('success');
      setBuildPending(false);
      onCreateBuildSuccess();
      setTimeout(() => {
        onShowBuildRating && onShowBuildRating();
      }, 3000);
      navigate(routes.Project, { projectId });
    }
  }, [
    uploadResult,
    projectId,
    projectName,
    navigate,
    setToastMessage,
    setToastSeverity,
    onShowBuildRating,
    onCreateBuildSuccess,
  ]);

  useEffect(() => {
    const error = configError || uploadError;
    if (error) {
      setToastMessage(error);
      setToastSeverity('error');
      setBuildPending(false);
    }
  }, [configError, uploadError, setToastMessage, setToastSeverity]);

  useEffect(() => {
    if (stepId !== 'apps') {
      navigate(routes.ProjectBuildStepper, { projectId, stepId: stepViews[0].type });
    }
    // run only once on mount to make sure to start on first step
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className={classes.root}>
      <Divider sx={{ margin: '16px 0' }} />
      <div className={classes.stepActions}>
        <div>
          <Text variant={ControllerFontType.Heading300B}>Build in Controller</Text>
        </div>
      </div>
      <div className={classes.steps}>
        <div>
          <Stepper nonLinear activeStep={activeStep} orientation='vertical'>
            {steps.map((step, index) => (
              <Step key={`${index}-${step.label}`} completed={step.complete}>
                <StepButton color='inherit' onClick={handleStep(index)} disabled={disableStepAtIdx(index)}>
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </div>

        {!configBusy && activeStep === 0 && <AppsStep config={config} onSelectApps={handleSelectApps} />}
        {!configBusy && activeStep === 1 && (
          <MediaStep config={config} showSelectSplash onSelectSplash={handleSelectSplash} />
        )}
        {!configBusy && activeStep === 2 && (
          <MediaStep config={config} showSelectAnimation onSelectAnimation={handleSelectAnimation} />
        )}
        {!configBusy && activeStep === 3 && (
          <ConfigureStep projectName={projectName} config={config} onConfigChange={handleConfigChange} />
        )}
        {!configBusy && activeStep === 4 && (
          <ReviewStep
            config={config}
            invalidArtifacts={invalidArtifacts}
            onInvalidArtifacts={handleInvalidArtifacts}
          />
        )}
      </div>
      <Alert className={classes.toolbar} variant='filled' severity='info' icon={<span />}>
        <Box display='flex' justifyContent='space-between'>
          <Button
            data-gtm-id={`Build-in-controller-cancel`}
            onClick={handleCancel}
            variant='outlined'
            sx={(theme) => ({
              color: theme.palette.secondary.contrastText,
              borderColor: theme.palette.secondary.contrastText,
            })}
          >
            Cancel
          </Button>
          <div>
            {activeStep < stepViews.length - 1 && (
              <Button
                data-gtm-id={`Build-in-controller-next`}
                variant='contained'
                size='large'
                onClick={handleStep(activeStep + 1)}
                disabled={configBusy || disableStepAtIdx(activeStep + 1)}
              >
                Next
              </Button>
            )}
            {activeStep === stepViews.length - 1 && (
              <Button
                data-gtm-id={`Build-in-controller-submit-build`}
                variant='contained'
                size='large'
                disabled={uploadBusy || configBusy || buildPending || disableStepAtIdx(activeStep + 1)}
                onClick={handleBuild}
              >
                Build
              </Button>
            )}
          </div>
        </Box>
      </Alert>
      <Modal
        disablePortal
        open={!uploadError && buildPending}
        sx={{ position: 'absolute' }}
        BackdropProps={{ sx: { position: 'absolute' } }}
      >
        <Box
          width='100%'
          height='100%'
          display='flex'
          justifyContent='center'
          alignItems='center'
          color='white'
        >
          <strong>{`Creating a build for ${projectName}`}</strong>
          <CircularProgress size='1em' color='inherit' sx={{ marginLeft: 1 }} />
        </Box>
      </Modal>
    </section>
  );
}

interface ConfigApkInfo {
  name: string;
  package_name: string;
  version_code: number;
}

interface BuildStepperProps {
  projectId: string;
  projectName: string;
  projectBuilds: JobModel[];
  stepId?: StepId;
  onCreateBuildSuccess: () => void;
  onShowBuildRating?: () => void;
}
